import useCheckPC from "@repo/hooks/utils/useCheckPC";
import type { IOrdersEdgesNode } from "@repo/shopify/src/queries/customer-gql";
import { transformDate } from "@repo/utils/src/number";
import classNames from "classnames";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import Button from "@components/atoms/Button";
import OrderListItem from "@components/molecules/ListItem/OrderListItem";

import LoadingIcon from "@atoms/LoadingIcon";

import Loading from "@molecules/Loading";

import useLayout from "@hooks/reducerHooks/useLayout";
import useMeta from "@hooks/reducerHooks/useMeta";
import useOrders from "@hooks/useOrders";

import { URLS } from "@const/urls";

export default function OrderHistory() {
  const { isPC } = useCheckPC();
  const { t } = useTranslation();
  const { setMetaTag } = useMeta();
  const { showFooter } = useLayout();

  const { data, loading, loaderEl, orders, hasNextPage, firstIntersecting } = useOrders();

  useEffect(() => {
    setMetaTag({ title: `TUNE.KR - ORDER HISTORY` });
  }, []);

  useEffect(() => {
    showFooter(!hasNextPage);
  }, [hasNextPage]);

  return (
    <>
      {!data?.customer && loading ? (
        <Loading />
      ) : (
        <>
          <section className="tune-column-4 pc:-mt-0 -mt-5">
            {orders.length ? (
              <ul className="flex w-full flex-col gap-5">
                {orders.map(({ node }: { node: IOrdersEdgesNode }) => (
                  <li className="relative" key={node.id}>
                    <OrderListItem
                      id={node.id}
                      image={node.lineItems.edges[0].node.variant ? node.lineItems.edges[0].node.variant.image : null}
                      lineTitle={node.lineItems.edges[0].node.title}
                      name={node.name}
                      order={node}
                      orderCreatedAt={transformDate(node.processedAt)}
                      totalPrice={parseInt(node.totalPrice?.amount ?? "0")}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <div className={classNames("flex flex-col items-center justify-center gap-5", !isPC && "mt-5")}>
                <span>{t("account.order.none")}</span>
                <a href={URLS.HOME}>
                  <Button styleType="primary" variant="contained">
                    View All Products
                  </Button>
                </a>
              </div>
            )}

            <div className="w-full" ref={loaderEl} />
          </section>

          {orders.length && hasNextPage ? (
            <>
              <div
                className={classNames(
                  "absolute top-0 right-0 bottom-0 left-0 bg-white transition-opacity duration-200",
                  loading ? "z-20 opacity-70" : "-z-10 opacity-0",
                  { invisible: firstIntersecting.current },
                )}
              />
              <section className="tune-column-4 flex items-center justify-center pb-10">
                <LoadingIcon type="spinner" />
              </section>
            </>
          ) : null}
        </>
      )}
    </>
  );
}
